// ── Polyline bulge (arc segment) geometry ────────────────────────────────────
// Single source of truth for the bulge→arc math used by rendering, bounds,
// hit-testing, grips and editing.

const EPS = 1e-6
export const hasBulge = b => Math.abs(b || 0) >= EPS

// Number of segments: a closed polyline has one extra (last vertex → first).
export const segmentCount = (points, closed) => (closed ? points.length : points.length - 1)

// Bulge array padded/truncated to exactly one value per segment.
export const bulgeArray = (points, bulge, closed) =>
  Array.from({ length: segmentCount(points, closed) }, (_, i) => bulge?.[i] || 0)

// Endpoints of segment `si` (wraps for the closing segment).
export const segmentEnds = (points, si) => [points[si], points[(si + 1) % points.length]]

// DXF convention: bulge = tan(sweep/4), positive = counter-clockwise from p1 to
// p2, so a positive bulge bows to the right of the p1→p2 direction.
// Center, radius, start angle and signed sweep of the arc p1→p2 with bulge b.
export function bulgeArc(p1, p2, b) {
  const dx = p2[0] - p1[0], dy = p2[1] - p1[1]
  const chord = Math.hypot(dx, dy)
  if (!chord || !hasBulge(b)) return null
  const sweep = 4 * Math.atan(b)
  const d = (chord / 2) / Math.tan(sweep / 2) // signed center offset along the left normal
  const cx = (p1[0] + p2[0]) / 2 - dy / chord * d, cy = (p1[1] + p2[1]) / 2 + dx / chord * d
  return { cx, cy, r: Math.hypot(p1[0] - cx, p1[1] - cy), start: Math.atan2(p1[1] - cy, p1[0] - cx), sweep }
}

// Point at fraction f (0..1) along the arc/segment p1→p2.
export function pointOnSegment(p1, p2, b, f) {
  const arc = bulgeArc(p1, p2, b)
  if (!arc) return [p1[0] + f * (p2[0] - p1[0]), p1[1] + f * (p2[1] - p1[1])]
  const a = arc.start + f * arc.sweep
  return [arc.cx + arc.r * Math.cos(a), arc.cy + arc.r * Math.sin(a)]
}

export function segMidpoint(p1, p2, b) {
  const mx = (p1[0] + p2[0]) / 2, my = (p1[1] + p2[1]) / 2
  if (!b) return [mx, my]
  const dx = p2[0] - p1[0], dy = p2[1] - p1[1]
  return [mx + b * dy / 2, my - b * dx / 2]
}

// Bulge that places the arc midpoint at `cursor` (inverse of segMidpoint).
export function bulgeFromCursor(p1, p2, cursor) {
  const mx = (p1[0] + p2[0]) / 2, my = (p1[1] + p2[1]) / 2
  const dx = p2[0] - p1[0], dy = p2[1] - p1[1]
  const chord = Math.hypot(dx, dy) || 1e-10
  const sag = (cursor[0] - mx) * (dy / chord) - (cursor[1] - my) * (dx / chord)
  return Math.max(-4, Math.min(4, 2 * sag / chord)) // clamp to avoid degenerate arcs
}

// Points along the arc p1→p2, excluding p1, including p2.
export function bulgeArcPoints(p1, p2, b) {
  const arc = bulgeArc(p1, p2, b)
  if (!arc) return [p2]
  const n = Math.max(16, Math.ceil(Math.abs(arc.sweep) / (Math.PI / 16)))
  return [...Array.from({ length: n - 1 }, (_, k) => pointOnSegment(p1, p2, b, (k + 1) / n)), p2]
}

// Polyline vertices with arcs tessellated in. For a closed polyline the closing
// arc is included too, minus its end point (points[0]) — callers close the loop.
export function tessellatePolyline(points, bulge, closed) {
  if (!bulge?.length) return points
  const out = [points[0]]
  for (let i = 0; i + 1 < points.length; i++) out.push(...bulgeArcPoints(points[i], points[i + 1], bulge[i]))
  const n = points.length
  if (closed && n > 1 && hasBulge(bulge[n - 1])) out.push(...bulgeArcPoints(points[n - 1], points[0], bulge[n - 1]).slice(0, -1))
  return out
}
